import {
  Component,
  Inject,
  OnInit,
  ViewChild,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialog,
} from '@angular/material/dialog';
import { AddReminderModalComponent } from '../../../shared/reminders/components/add-reminder-modal/add-reminder-modal.component';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { BackendApiService } from '../../../../services/backend-api.service';
import { ToasterService } from '../../../../services/core/toaster.service';
import { DatePipe, formatDate } from '@angular/common';
import { ObserverParticipantPopupComponent } from './add-checklist/observer-participant-popup/observer-participant-popup.component';
import { NgDialogAnimationService } from 'src/app/services/core/ng-dialog-animation.service';
import { TaskDataService } from '../task.data.service';
import { DialogService } from '../../../../services/core/dialog.service';
import { StaffService } from '../../../../services/staff/staff.service';
import { CommunicationServiceService } from '../../../widgets/services/communication-service.service';
import { UserService } from '../../../../services/core/user.service';
import { AppConstants } from '../../../../constants/app.constants';
import { resolveModuleResourcePath, MODULE_IDS } from 'src/app/constants/module-ids.constant';
import { TaskMainContentComponent } from './task-main-content/task-main-content.component';
import { ConfirmationPopupComponent } from '../../../shared/confirmation-popup/confirmation-popup.component';
import { TaskTagSelectorPopupComponent } from './task-tag-selector-popup/task-tag-selector-popup.component';
import { forkJoin, of, Observable, Subject } from 'rxjs';
import { switchMap, catchError, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { resolveStatusChange, saveTaskThenStatus } from './task-save-flow';

@Component({
    selector: 'app-add-task',
    templateUrl: './add-task.component.html',
    styleUrls: ['./add-task.component.scss'],
    standalone: false
})
export class AddTaskComponent implements OnInit, OnDestroy {
  public CONST_HIGH_PRIORITY: boolean = false;
  public CONST_TIME_PLANNING: boolean = false;
  public CONST_CREATED_BY: boolean = false;
  public CONST_OBSERVER: boolean = false;
  public CONST_PARTICIPANT: boolean = false;

  // Priority state memory for toggle
  private lastNonHighPriority: number = 1; // Default to Low when toggling off from High

  // UI State for new components
  isSidebarCollapsed = false;
  isFullscreen = false;
  isSearchOpen = false;
  searchQuery = '';
  activeTabIndex = 0;
  taskHistory: any[] = [];
  isFavorite = false; // Favorite state for footer quick action
  isSaving = false; // Loading state for save button
  // Set when the details saved but the follow-up status change failed, so a retry
  // with unchanged details re-sends only the status instead of the whole task again.
  private savedDetailsKey: string | null = null;
  private savedDetailsResponse: any = null;

  // ViewChild references
  @ViewChild(TaskMainContentComponent)
  taskMainContent!: TaskMainContentComponent;

  // Constants
  public TASK_MODULE_ID: number = 7;
  public CONST_RESPONSIBLE_PERSON_TYPE: string = 'responsible_person';
  public CONST_CREATED_BY_TYPE: string = 'created_by';
  public CONST_OBSERVER_TYPE: string = 'observer';
  public CONST_PARTICIPANT_TYPE: string = 'participant';

  // Data properties
  moduleID: number;
  referenceID: number;
  taskID: number;
  stageId: number;
  isJobSiteVisit: any;
  isTaskModule: boolean = false;
  isEdit: boolean = false;
  model: any = {};
  responsibleUsersArray: any = [];
  createdByArray: any;
  observerArray: any = [];
  participantArray: any = [];
  /** The logged-in user's saved default person per field (GET
   * tasks/default-people — read off task_associations.is_default), keyed by
   * 'assignee'/'observer'/'participant'. Only fetched/used for a brand-new
   * task, to pre-fill empty People fields. */
  savedDefaults: any = {};
  stages: any = [];
  templates: any = [];
  milestones: any = [];
  statuses: any = [];
  selectedStage: any = {};
  searchParams = {
    type: '',
    module_id: '',
    search: '',
  };
  selectedItem: any = {};
  showSearchInput: boolean = true;
  modulesList: any = [];
  mentions: any = [];
  selectedMentionedUsers: any = [];
  public allowedFileTypes: any = [];
  taskComments: any[] = []; // Comments for comments tab

  // Local tags state for CREATE flow (array of { name, color })
  taskTags: { id?: number; name: string; color: string }[] = [];

  // Attachment IDs for CREATE flow (uploaded attachments before task is saved)
  attachment_ids: number[] = [];

  // Mentions search management
  isMentionsLoading: boolean = false;
  private mentionSearchSubject = new Subject<string>();
  private mentionSearchSubscription: any;

  public lockModuleContext = false;
  public showModuleControls = true;
  private defaultProjectName?: string;
  /** Milestone id to pre-attach on create (passed from the timeline "+" button). */
  private preselectMilestoneId: number | null = null;

  public taskForm: UntypedFormGroup = new UntypedFormGroup({
    title: new UntypedFormControl(null, [Validators.required]),
    description: new UntypedFormControl(null),
    status: new UntypedFormControl(null), // Status ID - set after API loads
    priority: new UntypedFormControl(1), // 1=Low, 2=Medium, 3=High, 4=Urgent
    responsible_person: new UntypedFormControl([]),
    participants: new UntypedFormControl([]),
    observers: new UntypedFormControl([]),
    is_responsible_person: new UntypedFormControl(false),
    is_participant: new UntypedFormControl(false),
    is_observer: new UntypedFormControl(false),
    is_time_planning: new UntypedFormControl(false),
    due_date: new UntypedFormControl(null),
    start_date: new UntypedFormControl(null),
    date_finished: new UntypedFormControl(null),
    is_created_by: new UntypedFormControl(false),
    created_by: new UntypedFormControl(null),
    task_type_id: new UntypedFormControl(null),
    duration: new UntypedFormControl(null),
    checklists: new UntypedFormControl(null),
    is_template: new UntypedFormControl(false),
    stage_id: new UntypedFormControl(null),
    module_id: new UntypedFormControl(null),
    reference_id: new UntypedFormControl(null),
    milestone_id: new UntypedFormControl(null),
    self_assign: new UntypedFormControl(0),
  });
  constructor(
    public dialogRef: MatDialogRef<AddTaskComponent>,
    public backendApiService: BackendApiService,
    public toasterService: ToasterService,
    public dialog: NgDialogAnimationService,
    private matDialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public taskDataService: TaskDataService,
    public dialogService: DialogService,
    public staffService: StaffService,
    public scrumCommunication: CommunicationServiceService,
    private currentUser: UserService
  ) {
    this.moduleID = this.data?.moduleID;
    this.referenceID = this.data?.referenceID;
    this.stageId = this.data?.stageId;
    this.isTaskModule = this.data?.isTaskModule;
    this.isEdit = this.data?.isEdit;
    this.model = this.data?.model;
    this.taskID = this.data?.id;
    this.allowedFileTypes = AppConstants.documentFileTypes;
    this.isJobSiteVisit = this.data?.is_jobsite_visit;
    this.defaultProjectName = this.data?.defaultProjectName;
    // Milestone to pre-attach when opened from a milestone's "+" (create mode).
    this.preselectMilestoneId = this.data?.milestoneId ?? null;
    this.lockModuleContext = this.isModuleContextLocked();
    this.showModuleControls = !this.lockModuleContext;
    // Initialize favorite state in edit when model provided
    if (this.isEdit && this.model && this.model.hasOwnProperty('is_favorite')) {
      this.isFavorite = !!this.model.is_favorite;
    }
  }

  private isModuleContextLocked(): boolean {
    const moduleId = this.moduleID;
    const referenceId = this.referenceID;
    const hasModule = moduleId !== null && moduleId !== undefined;
    const hasReference = referenceId !== null && referenceId !== undefined;
    // Return true (lock/hide module controls) when:
    // - Has both moduleID and referenceID
    // - AND moduleID is NOT 11 (HR Staff should also hide like Tickets)
    return hasModule && hasReference;
  }

  ngOnInit(): void {
    // Ensure self-assign starts OFF in create mode
    this.taskForm.get('self_assign')?.setValue(0);
    this.taskForm.get('module_id')?.setValue(this.moduleID);
    this.taskForm.get('reference_id')?.setValue(this.referenceID);
    this.getModules();
    this.getStatuses();
    if (this.moduleID) {
      this.getStagesOfModule(this.moduleID);
    }
    // Load milestones if we have both moduleID and referenceID
    if (this.moduleID && this.referenceID) {
      this.loadMilestones();
    }
    this.getTaskTemplates();

    // Initialize mentions: Load initial users for dropdown
    this.loadInitialMentions();

    // Setup debounced mention search
    this.setupMentionSearch();

    // if (this.isJobSiteVisit === 1 && !this.isEdit) {
    //   const title = this.data?.jobsiteTitle || '';
    //   this.taskForm.get('title')?.setValue(title);
    //   const addr = this.data?.jobsiteAddress || '';
    //   this.taskForm
    //     .get('description')
    //     ?.setValue(addr ? `Jobsite : ${addr}` : 'Jobsite: ');
    // }

    if (this.isJobSiteVisit === 1 && !this.isEdit) {
      // Use the new props if available, otherwise fall back to existing logic
      const title = this.data?.taskTitle || this.data?.jobsiteTitle || '';
      const description =
        this.data?.taskDescription ||
        (this.data?.jobsiteAddress
          ? `Jobsite : ${this.data.jobsiteAddress}`
          : 'Jobsite: ');

      this.taskForm.get('title')?.setValue(title);
      this.taskForm.get('description')?.setValue(description);
    }

    // Pre-select the People fields the moment this modal opens, on a
    // brand-new task only — not waiting for "+" to be clicked. Editing an
    // existing task doesn't need this fetch at all: its own
    // responsible_persons/observers/participants already carry the real,
    // persisted is_default flag straight from task_associations.
    if (!this.isEdit) {
      this.backendApiService.getApi('tasks/default-people').subscribe({
        next: (response: any) => {
          this.savedDefaults = response?.data || {};
          this.applyDefaultPeopleSelection();
        },
        error: () => this.applyDefaultPeopleSelection(),
      });
    }

    // Add value change subscriptions for date validation
    this.taskForm.get('start_date')?.valueChanges.subscribe((value) => {
      if (value) {
        this.validateDates();
      }
    });

    this.taskForm.get('date_finished')?.valueChanges.subscribe((value) => {
      if (value) {
        this.validateDates();
      }
    });
  }

  ngAfterViewInit() {
    if (this.isEdit) {
      this.setDataOnEdit();
    }
  }

  setDataOnEdit() {
    // CRITICAL FIX: Check if model data is incomplete (corrupted/lost between constructor and here)
    // Fixed: Check for id AND essential properties to determine if model is complete
    const hasCompleteData =
      this.model?.id &&
      (this.model?.name || this.model?.title) &&
      this.model?.hasOwnProperty('description');

    if (!hasCompleteData && this.taskID) {
      // Fetch complete task data from API
      this.backendApiService.getApi(`tasks/${this.taskID}`).subscribe({
        next: (response: any) => {
          this.model = response?.data;
          // Now call setDataOnEdit again with complete data
          this.populateFormFromModel();
        },
        error: (err: any) => {
          console.error('[setDataOnEdit] Failed to fetch task data:', err);
          this.toasterService.showError('Failed to load task data');
        },
      });
      return; // Exit early, will continue in populateFormFromModel()
    }

    this.populateFormFromModel();
  }

  private populateFormFromModel() {
    if (this.model?.priority == 3) {
      this.changePriority();
    }

    // Use patchValue instead of setValue to allow partial updates
    this.taskForm.patchValue({
      title: this.model?.name || this.model?.title,
      description: this.model?.description,
      status: this.model?.status || null, // Use numeric status ID from model
      priority: this.model?.priority || 1, // Use actual numeric priority or default to Low
      responsible_person: [],
      participants: [],
      observers: [],
      is_responsible_person: this.model?.is_responsible_person,
      is_participant: this.model?.is_participant,
      is_observer: this.model?.is_observer,
      is_time_planning: this.model?.is_time_planning,
      due_date: this.model?.due_date
        ? formatDate(this.model.due_date, 'yyyy-MM-dd', 'en-US')
        : null,
      start_date: this.model?.start_date
        ? formatDate(this.model.start_date, 'yyyy-MM-dd', 'en-US')
        : null,
      date_finished: this.model?.date_finished
        ? formatDate(this.model?.date_finished, 'yyyy-MM-dd', 'en-US')
        : null,
      is_created_by: this.model?.is_created_by,
      created_by: this.model?.created_by ? this.model?.created_by?.id : null,
      task_type_id: 1,
      duration: this.model?.duration,
      checklists: this.model?.check_list ? this.model?.check_list : [],
      is_template: this.taskID ? this.model?.is_template : false,
      stage_id: this.model?.stage?.id,
      module_id: this.model?.module?.id || null,
      reference_id: this.model?.reference?.id || null,
      milestone_id:
        this.model?.module_data_milestone_id ||
        this.model?.milestone_id ||
        null,
      self_assign: 0, // Ensure self_assign is set for edit mode
    });

    // Debug log to verify date values
    console.log('[ADD-TASK] Date values when editing:', {
      due_date_raw: this.model?.due_date,
      start_date_raw: this.model?.start_date,
      date_finished_raw: this.model?.date_finished,
      due_date_formatted: this.model?.due_date
        ? formatDate(this.model.due_date, 'yyyy-MM-dd', 'en-US')
        : null,
      start_date_formatted: this.model?.start_date
        ? formatDate(this.model.start_date, 'yyyy-MM-dd', 'en-US')
        : null,
    });

    // Populate the description editor with the model description
    // Note: We need to use setTimeout to ensure the ViewChild is initialized
    setTimeout(() => {
      if (this.taskMainContent && this.model?.description) {
        this.taskMainContent.setDescriptionContent(
          this.model.description,
          this.selectedMentionedUsers
        );
      }
    }, 0);

    this.selectedStage = this.model?.stage;

    // Populate selectedItem from model in edit mode
    if (this.model?.reference) {
      this.selectedItem = this.model.reference;
      this.showSearchInput = false;
    }

    // Load milestones for edit mode if we have module and reference
    if (this.model?.module?.id && this.model?.reference?.id) {
      this.moduleID = this.model.module.id;
      this.referenceID = this.model.reference.id;
      // Store the milestone_id to be set after milestones are loaded
      const milestoneId = this.model?.milestone_id;
      this.loadMilestones();
      // The milestone will be selected in the loadMilestones callback
    }

    this.createdByArray = this.model?.created_by;
    this.CONST_CREATED_BY = true;
    this.CONST_OBSERVER = true;
    this.CONST_PARTICIPANT = true;

    this.model?.responsible_persons?.map((data: any) => {
      this.responsibleUsersArray.push({
        id: data?.id,
        full_name: data?.full_name,
        isDefault: data?.is_default,
      });
    });

    this.model?.observers?.map((data: any) => {
      this.observerArray.push({ id: data?.id, full_name: data?.full_name, isDefault: data?.is_default });
    });

    this.model?.participants?.map((data: any) => {
      this.participantArray.push({ id: data?.id, full_name: data?.full_name, isDefault: data?.is_default });
    });

    this.model?.mentions?.map((data: any) => {
      this.selectedMentionedUsers.push({
        id: data?.id,
        full_name: data?.full_name,
      });
    });

    // Initialize completion percentage based on current task state
    this.completionPercentage = this.calculateCompletionPercentage();

    // Load comments for edit mode
    this.getTaskComments();

    // Sync favorite state from loaded model
    this.isFavorite = !!this.model?.is_favorite;

    // Populate tags from model
    if (this.model?.tags && Array.isArray(this.model.tags)) {
      this.taskTags = this.model.tags.map((tag: any) => ({
        id: tag.id ?? tag.tag_id,
        name: tag.name || tag.tag_name || tag.tag || '',
        color: tag.color || tag.tag_color || 'gray',
      }));
    }
  }

  getStagesOfModule(id: any) {
    this.backendApiService
      .getApi('stages?module_id=' + id)
      .subscribe((response: any) => {
        this.stages = response.data;

        // If a stageId was passed, pre-select that stage
        if (this.stageId && this.stages && this.stages.length > 0) {
          const preSelectedStage = this.stages.find(
            (stage: any) => stage.id === this.stageId
          );
          if (preSelectedStage) {
            this.selectedStage = preSelectedStage;
            this.taskForm.get('stage_id')?.setValue(preSelectedStage.id);
          }
        }
      });
  }

  getTaskTemplates() {
    this.backendApiService
      .postApi('task-template/search', '')
      .subscribe((response: any) => {
        this.templates = response.data;
      });
  }

  getStatuses() {
    this.backendApiService.getApi('statuses/task').subscribe({
      next: (response: any) => {
        this.statuses = response?.data || [];

        // Set default status if creating new task (not editing)
        if (!this.isEdit && this.statuses.length > 0) {
          const defaultStatus = this.statuses.find(
            (s: any) => s.default_status === true || s.default_status === 1
          );
          if (defaultStatus) {
            this.taskForm.get('status')?.setValue(defaultStatus.id);
          } else if (this.statuses.length > 0) {
            // Fallback to first status if no default is set
            this.taskForm.get('status')?.setValue(this.statuses[0].id);
          }
        }
      },
      error: (err: any) => {
        console.error('Failed to load statuses:', err);
        this.statuses = [];
      },
    });
  }

  loadMilestones() {
    // Call the same API used in milestone timeline
    this.backendApiService
      .getApi(`module-data-milestones/${this.moduleID}/${this.referenceID}`)
      .subscribe(
        (response: any) => {
          if (response?.data && Array.isArray(response.data)) {
            // Transform API data to match TaskMilestone interface
            this.milestones = response.data.map((m: any) =>
              this.transformMilestoneData(m)
            );

            console.log('Loaded and transformed milestones:', this.milestones);

            // If editing and milestone was previously selected, restore selection
            const milestoneId =
              this.model?.module_data_milestone_id || this.model?.milestone_id;
            if (this.isEdit && milestoneId) {
              // Find the milestone in the loaded list
              const selectedMilestone = this.milestones.find(
                (m: any) => m.id === milestoneId
              );
              if (selectedMilestone) {
                // Use setTimeout to ensure ng-select has processed the items
                setTimeout(() => {
                  this.taskForm.get('milestone_id')?.setValue(milestoneId);
                  this.taskForm.get('milestone_id')?.updateValueAndValidity();
                }, 100);
              }
            }

            // Create mode: pre-attach the milestone passed from the timeline "+"
            // button, if it exists in the loaded list.
            if (!this.isEdit && this.preselectMilestoneId) {
              const preselected = this.milestones.find(
                (m: any) => m.id === this.preselectMilestoneId
              );
              if (preselected) {
                setTimeout(() => {
                  this.taskForm.get('milestone_id')?.setValue(this.preselectMilestoneId);
                  this.taskForm.get('milestone_id')?.updateValueAndValidity();
                }, 100);
              }
            }
          } else {
            this.milestones = [];
          }
        },
        (error) => {
          console.error('Error loading milestones:', error);
          this.milestones = [];
        }
      );
  }

  /**
   * Load reference details when dialog opens with pre-selected reference IDs
   * Fetches the actual record (bid/job/proposal) to populate selectedItem
   */
  loadReferenceDetails(): void {
    const moduleId = this.taskForm.get('module_id')?.value;
    const referenceId = this.taskForm.get('reference_id')?.value;

    // Skip if already have selectedItem or missing required IDs
    if (!moduleId || !referenceId || this.selectedItem?.id) {
      return;
    }

    // Find module to resolve its REST resource path (e.g. 'bids', 'jobs',
    // 'warehouses', 'subcontractors'). Some modules' backend slug is the
    // PascalCase display name, so resolve via resolveModuleResourcePath.
    const module = this.modulesList.find((m: any) => m.id === moduleId);

    const resourcePath = resolveModuleResourcePath(module);
    if (!resourcePath) {
      return;
    }

    const endpoint = `${resourcePath}/${referenceId}`;

    // Fetch reference details: GET {slug}/{id}
    this.backendApiService.getApi(endpoint).subscribe({
      next: (response: any) => {
        this.selectedItem = response?.data || {};
        this.showSearchInput = false;
      },
      error: (err: any) => {},
    });
  }

  /**
   * Transform API milestone data to match TaskMilestone interface
   * Converts snake_case properties to camelCase and calculates missing values
   * @param apiMilestone Raw milestone from API
   * @returns Transformed milestone matching TaskMilestone interface
   */
  private transformMilestoneData(apiMilestone: any): any {
    // Calculate task stats from tasks array
    const tasks = apiMilestone.tasks || [];
    const completedTasks = tasks.filter(
      (task: any) =>
        task.status === 'completed' ||
        task.status_name?.toLowerCase() === 'completed' ||
        task.completed_at !== null
    );

    // Determine or validate status
    let status = apiMilestone.status;
    if (
      !status ||
      !['upcoming', 'in-progress', 'completed', 'overdue'].includes(status)
    ) {
      if (apiMilestone.completed_at) {
        status = 'completed';
      } else if (
        apiMilestone.due_date &&
        new Date(apiMilestone.due_date) < new Date()
      ) {
        status = 'overdue';
      } else if (apiMilestone.progress > 0 || completedTasks.length > 0) {
        status = 'in-progress';
      } else {
        status = 'upcoming';
      }
    }

    // Calculate progress if not provided
    let progress = apiMilestone.progress;
    if (progress === undefined && tasks.length > 0) {
      progress = Math.round((completedTasks.length / tasks.length) * 100);
    }

    // Calculate days remaining
    let daysRemaining = apiMilestone.days_left;
    if (daysRemaining === undefined && apiMilestone.due_date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dueDate = new Date(apiMilestone.due_date);
      dueDate.setHours(0, 0, 0, 0);
      const diffTime = dueDate.getTime() - today.getTime();
      daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }

    // Return transformed milestone
    const transformed = {
      id: apiMilestone.id,
      name:
        apiMilestone.milestone_name || apiMilestone.name || 'Unnamed Milestone',
      dueDate: apiMilestone.due_date || apiMilestone.dueDate,
      status: status,
      startDate: apiMilestone.start_date || apiMilestone.startDate,
      endDate: apiMilestone.end_date || apiMilestone.endDate,
      progress: progress,
      taskStats:
        tasks.length > 0
          ? {
              total: tasks.length,
              completed: completedTasks.length,
            }
          : undefined,
      daysRemaining: daysRemaining,
      description: apiMilestone.description,
      assignedTo: apiMilestone.assigned_to_name || apiMilestone.assignedTo,
    };
    return transformed;
  }

  getModules() {
    this.backendApiService
      .getApi('modules/type?type=task')
      .subscribe((response: any) => {
        this.modulesList = response?.data;

        // Fetch reference details after modulesList is populated
        if (this.moduleID && this.referenceID) {
          this.loadReferenceDetails();
        }
      });
  }

  addStage(stage: any) {
    // console.log('stage',stage);
    this.selectedStage = stage;
    this.taskForm.get('stage_id')?.setValue(stage?.id);
  }

  /**
   * Flag chosen on the Create Task page. Nothing is sent while the task is being created: it is only
   * remembered here and saved by one request when the Create button is clicked and the task exists.
   */
  private newTaskFlag: { reason: string } | null = null;

  /** True when the task is flagged: the saved flag when editing, the chosen-but-not-yet-saved one when creating. */
  get isTaskFlagged(): boolean {
    return this.taskID ? !!this.model?.is_flagged : !!this.newTaskFlag;
  }

  /** The reason shown in the flag button's tooltip. */
  get taskFlagText(): string {
    return this.taskID ? (this.model?.flag_text || '') : (this.newTaskFlag?.reason || '');
  }

  /**
   * Save in the flag popup. Editing: flags or unflags the task and stores the optional reason, in one request.
   * Creating: only remembers the choice (no request) until the task is created. Clicking the flag button alone sends nothing.
   */
  saveFlag({ isFlagged, reason }: { isFlagged: boolean; reason: string }): void {
    if (!this.taskID) {
      this.newTaskFlag = isFlagged ? { reason } : null;
      return;
    }
    this.backendApiService.postApi(`tasks/update-flag/${this.taskID}`, { is_flagged: isFlagged, reason }).subscribe({
      next: (response: any) => {
        this.model = {
          ...this.model,
          is_flagged: isFlagged ? 1 : 0,
          flag_text: isFlagged && reason ? reason : null,
          unflag_text: !isFlagged && reason ? reason : null,
        };
        this.toasterService.showSuccessMessage(response?.data?.message || (isFlagged ? 'Task flagged successfully!' : 'Flag removed successfully!'));
      },
    });
  }

  /** Saves the optional reason typed in the flag popup (only called when the user typed one). */
  saveFlagReason(reason: string): void {
    if (!this.taskID) { return; }
    this.backendApiService.postApi(`tasks/update-flag/${this.taskID}`, { reason }).subscribe({
      next: (response: any) => {
        this.model = this.model?.is_flagged
          ? { ...this.model, flag_text: reason }
          : { ...this.model, unflag_text: reason };
        this.toasterService.showSuccessMessage(response?.data?.message || 'Reason saved successfully!');
      },
    });
  }

  changePriority(event: any = '') {
    const currentPriority = this.taskForm.get('priority')?.value;

    if (currentPriority === 3) {
      // Toggle OFF: Restore previous non-high priority
      this.taskForm.get('priority')?.setValue(this.lastNonHighPriority);
      this.CONST_HIGH_PRIORITY = false;
    } else {
      // Toggle ON: Remember current priority, set to HIGH (3)
      this.lastNonHighPriority = currentPriority;
      this.taskForm.get('priority')?.setValue(3);
      this.CONST_HIGH_PRIORITY = true;
    }
  }

  hideShowTimePlanning() {
    this.CONST_TIME_PLANNING = !this.CONST_TIME_PLANNING;
  }

  hideShowCreatedBy() {
    this.CONST_CREATED_BY = !this.CONST_CREATED_BY;
  }

  hideShowObserver() {
    this.CONST_OBSERVER = !this.CONST_OBSERVER;
  }

  hideShowParticipant() {
    this.CONST_PARTICIPANT = !this.CONST_PARTICIPANT;
  }

  getFormData() {
    const form = this.taskForm.value;
    const selectedStatus = this.getSelectedStatus();
    const selectedStatusId =
      form?.status?.id ??
      form?.status ??
      selectedStatus?.id ??
      this.model?.status ??
      this.model?.status_label?.id ??
      null;
    const selectedStatusName =
      selectedStatus?.name ??
      form?.status?.name ??
      this.model?.status_label?.name ??
      null;
    const colorHexMap: any = {
      blue: '#3b82f6',
      green: '#22c55e',
      yellow: '#eab308',
      red: '#ef4444',
      purple: '#a855f7',
      orange: '#f97316',
      gray: '#6b7280',
    };
    const tagsPayload = (this.taskTags || []).map((t: any) => ({
      tag: (t?.name ?? t?.tag ?? '').toString().trim(),
      color:
        colorHexMap[(t?.color || '').toLowerCase?.()] || t?.color || '#6b7280',
    }));
    return {
      // parent_id: this.referenceID,
      module_id: form.module_id,
      reference_id: form.reference_id,
      status: selectedStatusId,
      name: form.title,
      description: form.description,
      priority: form.priority, // Direct numeric value (1=Low, 2=Medium, 3=High, 4=Urgent)
      responsible_person: this.responsibleUsersArray?.map((x: any) => x.id),
      participants: this.participantArray?.map((x: any) => x.id),
      observers: this.observerArray.map((x: any) => x.id),
      // Whichever of the above (if any) currently has the star — persisted
      // as an is_default flag on that person's task_associations row, and
      // read back via GET tasks/default-people the next time a NEW task is
      // opened.
      default_responsible_person_id: this.responsibleUsersArray?.find((x: any) => x.isDefault)?.id ?? null,
      default_observer_id: this.observerArray?.find((x: any) => x.isDefault)?.id ?? null,
      default_participant_id: this.participantArray?.find((x: any) => x.isDefault)?.id ?? null,
      mentions: this.selectedMentionedUsers?.map((x: any) => x.id),
      is_participant: this.CONST_PARTICIPANT,
      is_observer: this.CONST_OBSERVER,
      is_time_planning: this.CONST_TIME_PLANNING,
      due_date: form?.due_date
        ? formatDate(form?.due_date, 'yyyy-MM-dd', 'en_US')
        : null,
      start_date: form?.start_date
        ? formatDate(form?.start_date, 'yyyy-MM-dd', 'en_US')
        : null,
      date_finished: form?.date_finished
        ? formatDate(form?.date_finished, 'yyyy-MM-dd', 'en_US')
        : null,
      is_created_by: this.CONST_CREATED_BY,
      created_by: form.created_by,
      task_type_id: form?.task_type_id ?? 1,
      stage_id: form?.stage_id,
      duration: form?.duration,
      check_list: form?.checklists,
      is_template: form.is_template,
      reminder: 'reminder',
      is_jobsite_visit: this.isJobSiteVisit === 1 ? 1 : 0,
      milestone_id: form?.milestone_id || null,
      self_assign: form?.self_assign ? 1 : 0,
      // Favorite flag for create/edit
      is_favorite: this.isFavorite ? 1 : 0,
      // Tags for CREATE payload
      tags: tagsPayload,
      // Attachment IDs for CREATE mode (attachments uploaded before task creation)
      attachment_ids: this.attachment_ids.length > 0 ? this.attachment_ids : undefined,
    };
  }

  validateDates() {
    const startDate = this.taskForm.get('start_date')?.value;
    const finishDate = this.taskForm.get('date_finished')?.value;

    if (startDate && finishDate) {
      const start = new Date(startDate);
      const finish = new Date(finishDate);

      if (start > finish) {
        this.taskForm.get('start_date')?.setErrors({ dateError: true });
        return false;
      } else {
        this.taskForm.get('start_date')?.setErrors(null);
        return true;
      }
    }
    return true;
  }

  save() {
    const form = this.taskForm.value;
    let isInvalid = false;
    if (!this.validateDates()) {
      isInvalid = true;
      this.toasterService.showWarning(
        'Date',
        'Start date cannot be greater than finish date.'
      );
    }
    if (!form.title) {
      isInvalid = true;
      this.toasterService.showWarning('Name', 'Name field is required.');
    }

    if (!form.reference_id) {
      isInvalid = true;
      this.toasterService.showWarning(
        'Reference',
        'Reference Number field is required.'
      );
    }

    if (!form.module_id) {
      isInvalid = true;
      this.toasterService.showWarning('Module', 'Module Field is Required.');
    }

    if (isInvalid) {
      return true;
    }

    // Set loading state
    this.isSaving = true;

    const payload = this.getFormData();
    const isUpdate = !!this.taskID;
    // PATCH tasks/{id} drops `status` (TaskController::getUpdateFormData), so a status picked in
    // the edit form is persisted afterwards through POST tasks/change-status/{id}.
    const statusId = isUpdate
      ? resolveStatusChange(
          this.model?.status ?? this.model?.status_label?.id,
          this.taskForm.get('status')?.value
        )
      : null;
    const detailsKey = JSON.stringify({ ...payload, status: undefined });
    const detailsAlreadySaved =
      isUpdate && statusId !== null && this.savedDetailsKey === detailsKey;

    saveTaskThenStatus(
      () =>
        detailsAlreadySaved
          ? of(this.savedDetailsResponse)
          : // Create new tags first (tags without IDs), then save task
            this.createNewSystemTags().pipe(
              switchMap(() =>
                isUpdate
                  ? this.backendApiService.patchApi(`tasks/${this.model?.id}`, payload)
                  : this.backendApiService.postApi('tasks/create', payload)
              )
            ),
      statusId,
      (id) =>
        this.backendApiService.postApi(`tasks/change-status/${this.model?.id}`, {
          status: id,
        })
    )
      .pipe(
        catchError((error) => {
          this.isSaving = false;
          throw error;
        })
      )
      .subscribe(({ detailsResponse, statusFailed, statusError }) => {
        this.isSaving = false;
        if (statusFailed) {
          // Keep the form open so the status change can be retried.
          this.savedDetailsKey = detailsKey;
          this.savedDetailsResponse = detailsResponse;
          const reason = statusError?.message ? ` (${statusError.message})` : '';
          this.toasterService.showErrorMessage(
            `The task details were saved, but the status change failed${reason}. Click Update Task to retry the status change.`
          );
          return;
        }
        this.savedDetailsKey = null;
        this.savedDetailsResponse = null;

        const finish = () => {
          this.isSaving = false;
          this.toasterService.showSuccessMessage(detailsResponse?.data?.message);
          this.close(true);
        };

        // A flag chosen on the Create Task page is saved now, by the one request that follows the create.
        const createdTaskId =
          detailsResponse?.data?.data?.id ?? detailsResponse?.data?.id ?? detailsResponse?.id;
        if (!isUpdate && this.newTaskFlag && createdTaskId) {
          const flag = this.newTaskFlag;
          this.isSaving = true;
          this.backendApiService
            .postApi(`tasks/update-flag/${createdTaskId}`, { is_flagged: true, reason: flag.reason })
            .pipe(catchError(() => of(null)))
            .subscribe((flagResponse) => {
              if (!flagResponse) {
                this.toasterService.showWarning('Flag', 'The task was created, but it could not be flagged.');
              }
              this.newTaskFlag = null;
              finish();
            });
          return;
        }
        if (!isUpdate && this.newTaskFlag) {
          // The create response carried no task id, so there is nothing to flag; say so instead of dropping it silently.
          this.toasterService.showWarning('Flag', 'The task was created, but it could not be flagged.');
          this.newTaskFlag = null;
        }
        finish();
      });
    return true;
  }

  /**
   * Create new tags as system tags before saving task
   * Tags without IDs are considered new and will be created
   */
  private createNewSystemTags(): Observable<any> {
    // Identify new tags (no ID)
    const newTags = this.taskTags.filter((tag) => !tag.id);

    if (newTags.length === 0) {
      return of(null); // No new tags to create
    }

    // Create all new tags as system tags
    const createRequests = newTags.map((tag) => {
      const colorHexMap: any = {
        blue: '#3b82f6',
        green: '#22c55e',
        yellow: '#eab308',
        red: '#ef4444',
        purple: '#a855f7',
        orange: '#f97316',
        gray: '#6b7280',
      };

      const payload = new FormData();
      payload.append('module_id', '7');
      payload.append('reference_id', '0'); // 0 for system tags
      payload.append('tag', tag.name);
      payload.append(
        'color',
        colorHexMap[tag.color?.toLowerCase()] || tag.color || '#6b7280'
      );
      payload.append('is_system', '1');

      return this.backendApiService.postApi('user/tags', payload);
    });

    // Wait for all tags to be created
    return forkJoin(
      createRequests.length > 0 ? createRequests : [of(null)]
    ).pipe(
      switchMap((responses: any[]) => {
        // Update taskTags with created tag IDs
        responses.forEach((response, index) => {
          if (response?.data?.id) {
            const tagIndex = this.taskTags.findIndex(
              (t) => !t.id && t.name === newTags[index].name
            );
            if (tagIndex > -1) {
              this.taskTags[tagIndex].id = response.data.id;
            }
          }
        });
        return of(null);
      })
    );
  }

  getChecklists(event: any) {
    this.taskForm.get('checklists')?.setValue(event);
    // Recalculate progress whenever checklists change
    this.completionPercentage = this.calculateCompletionPercentage();
  }

  openUsersPopup(event: any, type: any) {
    const position = event.target.getBoundingClientRect();
    const dialogRef = this.dialog.open(ObserverParticipantPopupComponent, {
      width: '420px',
      height: '500px',
      // position: {top:`${position.top}px`, left: `${position.left}px`},
      data: {
        type: type,
      },
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        if (result.type == this.CONST_RESPONSIBLE_PERSON_TYPE) {
          this.responsibleUsersArray = this.addUser(
            this.responsibleUsersArray,
            result.user
          );
          this.taskForm
            .get('responsible_users')
            ?.setValue(this.responsibleUsersArray);
        } else if (result.type == this.CONST_CREATED_BY_TYPE) {
          this.taskForm.get('created_by')?.setValue(result.user?.id);
          this.createdByArray = result.user;
          // this.createdByArray.push(result.user)
        } else if (result.type == this.CONST_OBSERVER_TYPE) {
          this.observerArray = this.addUser(this.observerArray, result.user);
        } else if (result.type == this.CONST_PARTICIPANT_TYPE) {
          this.participantArray = this.addUser(
            this.participantArray,
            result.user
          );
        }
      }
    });
  }

  addUser(usersList: any[], user: any): any[] {
    const alreadyExistCheck = usersList.find((x: any) => x.id == user.id);
    if (!alreadyExistCheck) {
      return [...usersList, user]; // Create new array reference for change detection
    }
    return usersList;
  }

  remove(index: any, dataArray: any) {
    dataArray.splice(index, 1);
  }

  selectedModule(event: any) {
    if (event) {
      this.searchParams.type = event?.slug;
      this.searchParams.module_id = event?.id;
      this.searchParams.search = '';
      this.selectedStage = {};
      this.milestones = [];
      this.taskForm.get('module_id')?.setValue(event?.id);
      this.taskForm.get('milestone_id')?.setValue(null);
      this.getStagesOfModule(event?.id);
    } else {
      this.taskForm.get('module_id')?.setValue('');
    }
    this.taskForm.get('reference_id')?.setValue('');
    this.showSearchInput = true;
    this.selectedItem = '';
  }

  onItemSelect(event: any) {
    this.selectedItem = event;
    this.taskForm.get('reference_id')?.setValue(event?.id);
    this.showSearchInput = false;

    // Load milestones when a reference is selected
    const moduleId = this.taskForm.get('module_id')?.value;
    if (moduleId && event?.id) {
      this.moduleID = moduleId;
      this.referenceID = event.id;
      this.loadMilestones();
    }
  }

  updateSearchInputFlag() {
    this.showSearchInput = true;
    this.taskForm.get('reference_id')?.setValue(null);
  }

  populateTemplate(templateSummary: any) {
    // Fetch complete template with all relations using show endpoint
    this.backendApiService
      .getApi(`task-template/${templateSummary.id}`)
      .subscribe(
        (response: any) => {
          if (response?.data) {
            this.populateFormFromTemplate(response.data);
            this.toasterService.showSuccessMessage(
              'Template loaded successfully'
            );
          }
        },
        (error: any) => {
          this.toasterService.showErrorMessage('Failed to load template');
          console.error('Template load error:', error);
        }
      );
  }

  /**
   * Populate current form with template data
   * @param template Complete template data with relations
   */
  private populateFormFromTemplate(template: any): void {
    // Set priority flag for UI
    if (template?.priority == 3) {
      this.CONST_HIGH_PRIORITY = true;
    }

    // Populate form fields - NOTE: is_template set to FALSE (creating new task)
    this.taskForm.patchValue({
      title: template?.name || '',
      description: template?.description || '',
      priority: template?.priority || 1,
      is_responsible_person: template?.is_responsible_person || false,
      is_participant: template?.is_participant || false,
      is_observer: template?.is_observer || false,
      is_time_planning: template?.is_time_planning || false,
      due_date: template?.due_date || null,
      start_date: template?.start_date || null,
      date_finished: template?.date_finished || null,
      is_created_by: false, // Reset for new task
      created_by: null, // Reset for new task
      task_type_id: 1,
      duration: template?.duration || null,
      checklists: template?.check_list || [],
      is_template: false, // Creating NEW task, not template
      stage_id: template?.stage?.id || null,
      module_id: template?.module?.id || null,
      reference_id: template?.reference?.id || null,
      milestone_id: template?.milestone_id || null,
    });

    // Populate selected stage
    this.selectedStage = template?.stage || {};

    // Populate selected reference/project
    if (template?.reference) {
      this.selectedItem = template.reference;
      this.showSearchInput = false;
    }

    // Populate user arrays with proper mapping
    this.responsibleUsersArray = [];
    template?.responsible_persons?.forEach((person: any) => {
      this.responsibleUsersArray.push({
        id: person?.id,
        full_name: person?.full_name,
      });
    });

    this.observerArray = [];
    template?.observers?.forEach((observer: any) => {
      this.observerArray.push({
        id: observer?.id,
        full_name: observer?.full_name,
      });
    });

    this.participantArray = [];
    template?.participants?.forEach((participant: any) => {
      this.participantArray.push({
        id: participant?.id,
        full_name: participant?.full_name,
      });
    });

    // Set visibility flags for UI sections
    this.CONST_OBSERVER = template?.is_observer || false;
    this.CONST_PARTICIPANT = template?.is_participant || false;

    // Load milestones if module and reference are set
    if (template?.module?.id && template?.reference?.id) {
      this.moduleID = template.module.id;
      this.referenceID = template.reference.id;
      this.loadMilestones();
    }
  }

  close = (data: any = null) => {
    // Details were saved but the status change failed: closing must still refresh the lists.
    if (!data && this.savedDetailsKey !== null) {
      data = true;
    }
    if (data) {
      this.scrumCommunication.setScrumBoardData(data);
    }
    this.dialogRef.close(data);
  };

  /**
   * Load initial mentions on component init
   * Fetches a default list of users to populate the dropdown
   */
  loadInitialMentions(): void {
    this.isMentionsLoading = true;
    this.backendApiService
      .getApi('staff/search?search=', [], false)
      .subscribe({
        next: (response: any) => {
          this.mentions = this.staffService.processStaff(response.data || []);
          this.isMentionsLoading = false;
          console.log('[MENTIONS] Initial load:', this.mentions.length, 'users'); // Debug log

          // Ensure dropdown is ready for initial data
          setTimeout(() => {
            if (this.taskMainContent) {
              this.taskMainContent.refreshMentionDropdown();
            }
          }, 100);
        },
        error: (err: any) => {
          console.error('[MENTIONS] Failed to load initial users:', err);
          this.mentions = [];
          this.isMentionsLoading = false;
        },
      });
  }

  /**
   * Setup debounced mention search with RxJS Subject
   * Prevents excessive API calls during fast typing
   */
  setupMentionSearch(): void {
    this.mentionSearchSubscription = this.mentionSearchSubject
      .pipe(
        debounceTime(300), // Wait 300ms after user stops typing
        distinctUntilChanged() // Only emit if value changed
      )
      .subscribe((searchQuery: string) => {
        this.performMentionSearch(searchQuery);
      });
  }

  /**
   * Perform actual mention search API call
   * @param searchQuery Search term from user input
   */
  performMentionSearch(searchQuery: string): void {
    this.isMentionsLoading = true;

    this.backendApiService
      .getApi(`staff/search?search=${searchQuery}`, [], false)
      .subscribe({
        next: (response: any) => {
          this.mentions = this.staffService.processStaff(response.data || []);
          this.isMentionsLoading = false;
          console.log('[MENTIONS] Search results for "' + searchQuery + '":', this.mentions.length, 'users'); // Debug log

          // Force Syncfusion to refresh the dropdown
          setTimeout(() => {
            if (this.taskMainContent) {
              this.taskMainContent.refreshMentionDropdown();
            }
          }, 50);
        },
        error: (err: any) => {
          console.error('[MENTIONS] Search failed:', err);
          this.mentions = [];
          this.isMentionsLoading = false;

          // Force refresh even on error to show "No results"
          setTimeout(() => {
            if (this.taskMainContent) {
              this.taskMainContent.refreshMentionDropdown();
            }
          }, 50);
        },
      });
  }

  /**
   * Handle mention search event from child component
   * Pushes search query to debounced Subject
   * @param searchQuery Search term from Syncfusion mention component
   */
  getUsers(event: any) {
    const search = typeof event === 'string' ? event : event?.text || '';
    // Push to subject for debounced processing
    this.mentionSearchSubject.next(search);
  }

  getSelectedUser(event: any) {
    // Handle both single user (from Syncfusion) and array (from child component)
    if (Array.isArray(event)) {
      // Array of mentioned users from child component
      this.selectedMentionedUsers = event;
    } else {
      // Single user selection from Syncfusion dropdown
      const index = this.selectedMentionedUsers.findIndex(
        (x: any) => x.id == event?.id
      );
      if (index == -1) {
        this.selectedMentionedUsers.push(event);
      }
    }
  }

  // Helper methods for new UI
  getProjectName(): string {
    // Prefer explicit project name provided by opener (e.g., Bid name)
    if (this.defaultProjectName && this.defaultProjectName.trim()?.length) {
      return this.defaultProjectName;
    }
    // Check all possible property variations across different modules
    const name =
      this.selectedItem?.name || // Standard name
      this.selectedItem?.data || // Search results format
      this.selectedItem?.title || // Alternative name property
      this.selectedItem?.bid_name || // Bid-specific
      this.selectedItem?.job_name || // Job-specific
      this.selectedItem?.proposal_name || // Proposal-specific
      this.selectedItem?.reference_number; // Fallback to reference number

    if (name) {
      return name;
    }

    // Fallback: If we have reference_id but no selectedItem details
    const referenceId = this.taskForm.get('reference_id')?.value;
    if (referenceId) {
      const moduleLabel = this.getModuleLabel();
      return `Selected ${moduleLabel}`;
    }

    return '';
  }

  getRecordNumber(): string {
    // Try various reference number property names
    const recordNum =
      this.selectedItem?.reference_number ||
      this.selectedItem?.ref_number ||
      this.selectedItem?.bid_number ||
      this.selectedItem?.job_number ||
      this.selectedItem?.proposal_number ||
      this.selectedItem?.number ||
      this.selectedItem?.id?.toString();

    if (recordNum) {
      return recordNum;
    }

    // Fallback: Use reference_id from form
    const referenceId = this.taskForm.get('reference_id')?.value;
    const result = referenceId?.toString() || '';
    return result;
  }

  /**
   * Detail-page routes for module ids a task's linked record can point to.
   * Mirrors view-task-detail.component.ts's LINKED_RECORD_ROUTES — kept in
   * sync with that map since both resolve the same module id the same way.
   */
  private readonly LINKED_RECORD_ROUTES: Partial<Record<number, (id: number | string) => string>> = {
    [MODULE_IDS.PRODUCT]: (id) => `/products/product/${id}`,
    [MODULE_IDS.STAFF]: (id) => `/hr/staff/${id}`,
    [MODULE_IDS.VENDOR]: (id) => `/vendors/vendor/${id}`,
    [MODULE_IDS.CUSTOMER]: (id) => `/customers/customer/${id}`,
    [MODULE_IDS.BID]: (id) => `/bids/bid/${id}`,
    [MODULE_IDS.WAREHOUSE]: (id) => `/warehouses/warehouse/${id}`,
    [MODULE_IDS.JOB]: (id) => `/jobs/detail/${id}`,
    [MODULE_IDS.CHANGE_ORDER]: (id) => `/change-order/edit/${id}`,
  };

  /**
   * Route to the task's linked module record in edit/create mode. Mirrors
   * getModuleLabel()'s edit-vs-create fallback: prefers the loaded model
   * (edit mode), falls back to the form's own selected module/reference
   * (create mode, before the model exists).
   */
  getRecordRoute(): string | null {
    const moduleId = this.model?.module?.id ?? this.taskForm.get('module_id')?.value;
    const recordId = this.model?.reference?.id ?? this.taskForm.get('reference_id')?.value;

    if (!moduleId || !recordId) {
      return null;
    }

    const buildRoute = this.LINKED_RECORD_ROUTES[moduleId];
    return buildRoute ? buildRoute(recordId) : null;
  }

  getModuleLabel(): string {
    // In edit mode, get from model.module
    if (this.model?.module?.name) {
      return this.model.module.name;
    }

    // In new mode, get from modulesList based on selected module_id
    const moduleId = this.taskForm.get('module_id')?.value;
    if (moduleId && this.modulesList) {
      const module = this.modulesList.find((m: any) => m.id === moduleId);
      return module?.name || 'Project';
    }

    return 'Project';
  }

  getSelectedMilestone() {
    const milestoneId = this.taskForm.get('milestone_id')?.value;
    return this.milestones.find((m: any) => m.id === milestoneId);
  }

  // Reactive property for task completion percentage
  completionPercentage: number = 0;

  /**
   * Calculate task progress based on status and checklist completion
   * Returns 100% if task is marked complete, otherwise checklist percentage
   */
  private calculateCompletionPercentage(): number {
    // Check if task is marked as complete via status
    const status = this.getStatusFromForm();
    if (this.isTaskComplete(status)) {
      return 100;
    }

    // Calculate based on checklist completion
    const checklist = this.taskForm.get('checklists')?.value || [];
    if (!checklist.length) return 0;
    const completed = checklist.filter(
      (item: any) => item.is_completed || item.completed
    ).length;
    return Math.round((completed / checklist.length) * 100);
  }

  /**
   * Check if a status indicates task completion
   */
  private isTaskComplete(status: any): boolean {
    if (!status) return false;

    // Check by status name or label (case-insensitive)
    const statusName = (
      status.name ||
      status.label ||
      status.status_name ||
      ''
    ).toLowerCase();

    // Common completion status names
    const completionKeywords = [
      'complete',
      'completed',
      'done',
      'finished',
      'closed',
    ];
    return completionKeywords.some((keyword) => statusName.includes(keyword));
  }

  getChecklistCount(): number {
    return (this.taskForm.get('checklists')?.value || []).length;
  }

  getAttachmentsCount(): number {
    // In edit mode, get count from model
    if (this.model?.relations?.attach_files) {
      return this.model.relations.attach_files.length;
    }
    // In create mode, return 0
    return 0;
  }

  /**
   * Get comments for the task
   * Called when editing existing task
   */
  getTaskComments(): void {
    if (this.taskID) {
      this.backendApiService
        .postApi(`tasks/get-comments/${this.taskID}`, '')
        .subscribe((response: any) => {
          this.taskComments = response?.data?.data || [];
        });
    }
  }

  /**
   * Handle comment added event from comments component
   * Refresh comment count in tab badge
   */
  handleCommentAdded(): void {
    if (this.taskID) {
      this.getTaskComments();
    }
  }

  getStatusText(): string {
    return this.selectedStage?.name || 'New';
  }

  /**
   * Get status from form
   * Returns the status value from the form control
   */
  getStatusFromForm(): string {
    // First, try to get status_name from the model (text representation)
    if (this.model?.status_name) {
      return this.model.status_name.toLowerCase();
    }

    // Fall back to form value
    const statusValue = this.taskForm.get('status')?.value;

    // If it's already a string, return it
    if (typeof statusValue === 'string') {
      return statusValue;
    }

    // Default to 'new' if no valid status found
    return 'new';
  }

  /**
   * Get the selected status object from the statuses array
   * Returns the full status object with color information
   */
  getSelectedStatus(): any {
    const statusId = this.taskForm.get('status')?.value;
    if (statusId && this.statuses && this.statuses.length > 0) {
      return this.statuses.find((s: any) => s.id === statusId);
    }
    return null;
  }

  /**
   * Convert numeric priority to display text
   * @param priority - Numeric priority (1=Low, 2=Medium, 3=High, 4=Urgent)
   * @returns Display text for priority
   */
  getPriorityText(priority: number): string {
    const priorityMap: { [key: number]: string } = {
      1: 'low',
      2: 'medium',
      3: 'high',
      4: 'urgent',
    };
    return priorityMap[priority] || 'medium';
  }

  handleSearch(query: string) {
    this.searchQuery = query;
  }

  // Header/sidebar event helpers to satisfy template type checking
  onSidebarCollapse(collapsed: boolean): void {
    this.isSidebarCollapsed = collapsed;
  }

  onHeaderSearchToggle(open: boolean): void {
    this.isSearchOpen = open;
  }

  onHeaderSearchChange(value: string): void {
    this.searchQuery = value;
  }

  onFullscreenToggle(full: boolean): void {
    this.isFullscreen = full;
  }

  handleFieldChange(event: any): void {
    // Handle sidebar field changes
    if (event.field && event.value !== undefined) {
      // Update form control if needed
      const control = this.taskForm.get(event.field);
      if (control) {
        control.setValue(event.value);
      }
    }
  }

  handleMilestoneSelect(milestone: any) {
    this.taskForm.get('milestone_id')?.setValue(milestone.id);
  }

  handleMilestoneRemove() {
    this.taskForm.get('milestone_id')?.setValue(null);
  }

  handleStatusChange(status: any) {
    if (status?.id) {
      this.taskForm.get('status')?.setValue(status.id);
      // Recalculate progress when status changes (might be marked complete)
      this.completionPercentage = this.calculateCompletionPercentage();
    }
  }

  handleEditDescription() {
    // Focus description field or switch to details tab
    this.activeTabIndex = 0;
  }

  handleTabChange(event: any) {
    // Handle tab change event from TaskMainContentComponent
    this.activeTabIndex = event.currentIndex || event;
  }

  removeAssignee(index: number) {
    this.responsibleUsersArray = [
      ...this.responsibleUsersArray.slice(0, index),
      ...this.responsibleUsersArray.slice(index + 1),
    ];
    this.taskForm
      .get('responsible_users')
      ?.setValue(this.responsibleUsersArray);
  }

  removeObserver(index: number) {
    this.observerArray = [
      ...this.observerArray.slice(0, index),
      ...this.observerArray.slice(index + 1),
    ];
  }

  removeParticipant(index: number) {
    this.participantArray = [
      ...this.participantArray.slice(0, index),
      ...this.participantArray.slice(index + 1),
    ];
  }

  /**
   * Handle assignee selection from inline search
   * @param user Selected user object
   */
  handleAssigneeSelected(user: any): void {
    this.responsibleUsersArray = this.addUser(this.responsibleUsersArray, user);
    this.taskForm
      .get('responsible_users')
      ?.setValue(this.responsibleUsersArray);
  }

  /**
   * Handle observer selection from inline search
   * @param user Selected user object
   */
  handleObserverSelected(user: any): void {
    this.observerArray = this.addUser(this.observerArray, user);
  }

  /**
   * Handle participant selection from inline search
   * @param user Selected user object
   */
  handleParticipantSelected(user: any): void {
    this.participantArray = this.addUser(this.participantArray, user);
  }

  /**
   * User clicked a different row's star in the picker dropdown to make THAT
   * one the default instead of whichever was positionally first. Swaps them
   * into the field: removes whoever is currently flagged isDefault (if the
   * user hadn't already removed them by hand), then adds the newly-picked
   * one in their place. Same shape for all three People fields.
   */
  /**
   * Fires both when the dropdown's star picks someone not yet selected, and
   * when the star next to an ALREADY-selected assignee is clicked to
   * promote them. In the latter case the person is already in
   * responsibleUsersArray, so remove-then-readd would silently no-op
   * (addUser's own dedupe check would just hand back the untouched array,
   * dropping the isDefault flag) — remap in place instead whenever that's
   * the case. Nothing is persisted here directly — whichever person ends up
   * flagged isDefault is included as default_*_id the next time
   * getFormData()/save() runs, and stored as a flag on that person's own
   * task_associations row.
   */
  handleDefaultAssigneeChanged(user: any): void {
    const alreadySelectedIndex = this.responsibleUsersArray.findIndex((x: any) => x.id === user.id);
    if (alreadySelectedIndex > -1) {
      this.responsibleUsersArray = this.responsibleUsersArray.map((x: any) => ({
        ...x,
        isDefault: x.id === user.id,
      }));
    } else {
      const index = this.responsibleUsersArray.findIndex((x: any) => x.isDefault);
      if (index > -1) {
        this.removeAssignee(index);
      }
      this.handleAssigneeSelected({ ...user, isDefault: true });
    }
  }

  handleDefaultObserverChanged(user: any): void {
    const alreadySelectedIndex = this.observerArray.findIndex((x: any) => x.id === user.id);
    if (alreadySelectedIndex > -1) {
      this.observerArray = this.observerArray.map((x: any) => ({
        ...x,
        isDefault: x.id === user.id,
      }));
    } else {
      const index = this.observerArray.findIndex((x: any) => x.isDefault);
      if (index > -1) {
        this.removeObserver(index);
      }
      this.handleObserverSelected({ ...user, isDefault: true });
    }
  }

  handleDefaultParticipantChanged(user: any): void {
    const alreadySelectedIndex = this.participantArray.findIndex((x: any) => x.id === user.id);
    if (alreadySelectedIndex > -1) {
      this.participantArray = this.participantArray.map((x: any) => ({
        ...x,
        isDefault: x.id === user.id,
      }));
    } else {
      const index = this.participantArray.findIndex((x: any) => x.isDefault);
      if (index > -1) {
        this.removeParticipant(index);
      }
      this.handleParticipantSelected({ ...user, isDefault: true });
    }
  }

  /**
   * Pre-selects Assignee/Observer/Participant with each field's saved
   * default (this.savedDefaults, from GET tasks/default-people, fetched
   * just before this is called) when the user has one. Falls back to the
   * picker's own "first result" for any field that doesn't have a saved
   * preference yet (first time ever using the picker).
   */
  private applyDefaultPeopleSelection(): void {
    const needsFallback =
      (!this.savedDefaults.assignee && this.responsibleUsersArray.length === 0) ||
      (!this.savedDefaults.observer && this.observerArray.length === 0) ||
      (!this.savedDefaults.participant && this.participantArray.length === 0);

    if (this.savedDefaults.assignee && this.responsibleUsersArray.length === 0) {
      this.handleAssigneeSelected({ ...this.savedDefaults.assignee, isDefault: true });
    }
    if (this.savedDefaults.observer && this.observerArray.length === 0) {
      this.handleObserverSelected({ ...this.savedDefaults.observer, isDefault: true });
    }
    if (this.savedDefaults.participant && this.participantArray.length === 0) {
      this.handleParticipantSelected({ ...this.savedDefaults.participant, isDefault: true });
    }

    if (!needsFallback) {
      return;
    }

    this.backendApiService
      .getApi('staff/search', { search: '', page: 1, per_page: 1 })
      .subscribe({
        next: (response: any) => {
          const fallbackUser = this.staffService.processStaff(response?.data || [])[0];
          if (!fallbackUser) {
            return;
          }
          const flagged = { ...fallbackUser, isDefault: true };
          if (!this.savedDefaults.assignee && this.responsibleUsersArray.length === 0) {
            this.handleAssigneeSelected(flagged);
          }
          if (!this.savedDefaults.observer && this.observerArray.length === 0) {
            this.handleObserverSelected(flagged);
          }
          if (!this.savedDefaults.participant && this.participantArray.length === 0) {
            this.handleParticipantSelected(flagged);
          }
        },
      });
  }

  onConfirmation() {
    if (!this.taskID) {
      return;
    }

    const dialogRef = this.matDialog.open(ConfirmationPopupComponent, {
      width: '500px',
      height: '300px',
      data: {},
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.backendApiService.deleteApi(`tasks/${this.taskID}`).subscribe(
          (response: any) => {
            this.toasterService.showSuccessMessage(
              response?.data?.message || 'Task deleted successfully'
            );
            this.close(true);
          },
          () => {
            this.toasterService.showError('Error', 'Failed to delete task');
          }
        );
      }
    });
  }

  /**
   * Handle self-assign toggle from sidebar (create mode)
   */
  handleSelfAssignChange(isOn: boolean): void {
    this.taskForm.get('self_assign')?.setValue(isOn ? 1 : 0);

    if (isOn) {
      const loginUserId = this.currentUser?.getID?.();
      this.taskForm.get('module_id')?.setValue(11);
      if (loginUserId) {
        this.taskForm.get('reference_id')?.setValue(Number(loginUserId));
      }
      // Clear milestones for self-assign
      this.milestones = [];
    } else {
      // Allow user to choose module/reference again
      this.taskForm.get('module_id')?.setValue(null);
      this.taskForm.get('reference_id')?.setValue(null);
    }
  }

  // Time Planning handlers
  handleStartDateChange(date: Date): void {
    if (date) {
      // Format as YYYY-MM-DD for backend
      const formattedDate = formatDate(date, 'yyyy-MM-dd', 'en_US');
      this.taskForm.get('start_date')?.setValue(formattedDate);
    }
  }

  handleDurationChange(duration: number): void {
    this.taskForm.get('duration')?.setValue(duration);
  }

  handleFinishDateChange(date: Date): void {
    if (date) {
      // Format as YYYY-MM-DD for backend
      const formattedDate = formatDate(date, 'yyyy-MM-dd', 'en_US');
      this.taskForm.get('date_finished')?.setValue(formattedDate);
    }
  }

  /**
   * Toggle favorite state for task
   * Footer quick action functionality
   * Uses /api/v1/user/favorites/add endpoint with module_id: 28 (Task module)
   */
  toggleFavorite(): void {
    if (!this.taskID) {
      this.toasterService.showWarning(
        'Please save the task first before adding to favorites'
      );
      return;
    }

    const previous = this.isFavorite;
    this.isFavorite = !this.isFavorite; // optimistic UI
    const payload = {
      reference_id: this.taskID,
      module_id: 7, // Fixed value for Task module
      is_favorite: this.isFavorite ? 1 : 0,
    };

    this.backendApiService.postApi('user/favorites/add', payload).subscribe(
      (response: any) => {
        this.toasterService.showSuccessMessage(
          this.isFavorite
            ? 'Task added to favorites'
            : 'Task removed from favorites'
        );
        // Update model if it exists
        if (this.model) {
          this.model.is_favorite = this.isFavorite ? 1 : 0;
        }
      },
      (error: any) => {
        // revert on error
        this.isFavorite = previous;
        this.toasterService.showError('Failed to update favorite state');
      }
    );
  }

  /**
   * Open reminder dialog/popup
   * Footer quick action functionality
   */
  setReminder(): void {
    // Only allow if task is saved (has ID)
    if (!this.taskID) {
      this.toasterService.showWarning('Please save the task first before setting a reminder');
      return;
    }

    const taskName = this.model?.name || this.model?.title || `Task #${this.taskID}`;
    const dialogData: any = {
      reminderable_type: 'App\\Models\\Task',
      reminderable_id: this.taskID,
      entityName: taskName
    };

    // Add location if present in task model
    if (this.model?.location) {
      dialogData.location = this.model.location;
    }

    const dialogRef = this.matDialog.open(AddReminderModalComponent, {
      panelClass: 'reminder-modal-panel',
      disableClose: false,
      autoFocus: true,
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.toasterService.showSuccessMessage('Reminder set successfully!');
      }
    });
  }

  // =========================
  // TAGS - CREATE AND EDIT MODE
  // =========================
  openTagSelector(): void {
    const dialogRef = this.dialog.open(TaskTagSelectorPopupComponent, {
      width: '500px',
      maxWidth: '500px',
      maxHeight: '80vh',
      panelClass: 'task-tag-selector-popup-dialog-container',
      data: { currentTags: this.taskTags },
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (!result || !result.tag) return;
      const selected = result.tag;
      const name: string = (selected?.name || selected?.tag || '')
        .toString()
        .trim();
      const color: string = selected?.color || 'gray';
      if (!name) return;
      // Prevent duplicates (case-insensitive)
      const exists = this.taskTags.some(
        (t) => t.name.toLowerCase() === name.toLowerCase()
      );
      if (exists) {
        this.toasterService.showInfo('Tag already added');
        return;
      }
      // Both CREATE and EDIT mode: Update local array only
      // Tags will be persisted when user clicks "Save" or "Create"
      this.taskTags = [...this.taskTags, { name, color }];
    });
  }

  removeTagByIndex(index: number): void {
    if (index < 0 || index >= this.taskTags.length) return;
    this.taskTags = [
      ...this.taskTags.slice(0, index),
      ...this.taskTags.slice(index + 1),
    ];
  }

  handleTagsUpdated(
    updatedTags: { id?: number; name: string; color: string }[]
  ): void {
    if (!Array.isArray(updatedTags)) {
      return;
    }
    this.taskTags = updatedTags.map((tag) => ({
      name: tag?.name || '',
      color: tag?.color || 'gray',
      ...(tag?.id ? { id: tag.id } : {}),
    }));
  }

  /**
   * Handle attachment upload in CREATE mode
   * Collects attachment IDs from uploaded files
   * NOTE: This rebuilds the entire array on each callback to handle deletions
   */
  handleAttachmentUpload(event: any): void {
    const attachmentData = event?.attachment;

    // Reset attachment_ids array
    this.attachment_ids = [];

    if (!attachmentData) {
      return;
    }

    // Handle multiple attachments (array)
    if (Array.isArray(attachmentData)) {
      attachmentData.forEach((file: any) => {
        const attachmentId = file.attachment_id || file.attach_file?.id;
        if (attachmentId) {
          this.attachment_ids.push(attachmentId);
        }
      });
    }
    // Handle single attachment
    else if (attachmentData.attachment_id) {
      this.attachment_ids.push(attachmentData.attachment_id);
    }

    console.log('Updated attachment IDs:', this.attachment_ids);
  }

  /**
   * Handle attachment deletion in CREATE mode
   * Removes attachment ID from attachment_ids array
   */
  handleAttachmentDelete(event: any): void {
    // Note: The app-attachment component handles server-side deletion
    // We just need to refresh our local attachment_ids array
    // This will be handled automatically when callback is emitted again
    console.log('Attachment deleted, attachment_ids will be updated on next callback');
  }

  /**
   * Cleanup subscriptions on component destroy
   */
  ngOnDestroy(): void {
    if (this.mentionSearchSubscription) {
      this.mentionSearchSubscription.unsubscribe();
    }
  }
}
